import {
  CommentMentionPayload,
  INotificationItem,
  NotificationEventType,
  NotificationPayload,
  OrderCreatedPayload,
  OrderStatusChangedPayload,
  ThreadAssignmentPayload,
  WorkflowSendReplyFailedPayload,
} from '../models/notification.models';

// ─────────────────────────────────────────────────────────────────────────────
// Notification Event Registry
// ─────────────────────────────────────────────────────────────────────────────
// One declaration per notification event type, owning its module label, title
// copy, title params, deep-link target and desktop eligibility. Adding an event
// type to NotificationEventType without adding it here fails the type check.
// ─────────────────────────────────────────────────────────────────────────────

export interface NotificationRoute {
  path: unknown[];
  queryParams?: Record<string, string | number>;
}

export interface DesktopNotificationSpec {
  /** Per-instance eligibility. A status this event should not notify about returns false. */
  eligible: (payload: NotificationPayload) => boolean;
  titleKey: (payload: NotificationPayload) => string;
  titleParams: (payload: NotificationPayload) => Record<string, unknown>;
  /** Identical in every tab for the same event, and across events about the same order. */
  tag: (payload: NotificationPayload) => string;
  /** Exact route paths on which a focused tab skips this event. */
  suppressOnRoutes: readonly string[];
}

export interface NotificationEventDescriptor {
  /** Null falls back to the notification's own source_module. */
  moduleLabelKey: string | null;
  titleKey: (payload: NotificationPayload | null) => string;
  titleParams: (payload: NotificationPayload | null) => Record<string, unknown>;
  route: (payload: NotificationPayload) => NotificationRoute;
  desktop: DesktopNotificationSpec | false;
}

const UNKNOWN_TITLE_KEY = 'notifications.event.unknown';

/**
 * Copy for a completed order, shared by the bell row and the desktop banner so the two
 * surfaces cannot describe the same event differently. Four variants because either the
 * order reference or the source subject can be missing, and the sentence has to read
 * correctly without each.
 */
function orderReadyTitleKey(order: OrderStatusChangedPayload | null): string {
  if (order?.order_name) {
    return order.email_subject
      ? 'notifications.event.order_ready_named_subject'
      : 'notifications.event.order_ready_named';
  }
  return order?.email_subject
    ? 'notifications.event.order_ready_subject'
    : 'notifications.event.order_ready';
}

function orderStatusTitleParams(order: OrderStatusChangedPayload | null): Record<string, unknown> {
  return { subject: order?.email_subject ?? '', orderName: order?.order_name ?? '' };
}

export const NOTIFICATION_EVENTS: Record<NotificationEventType, NotificationEventDescriptor> = {
  comment_mention: {
    moduleLabelKey: null,
    titleKey: (payload) => {
      const mention = payload as CommentMentionPayload | null;
      return mention?.thread_subject
        ? 'notifications.event.comment_mention_subject'
        : 'notifications.event.comment_mention';
    },
    titleParams: (payload) => ({
      subject: (payload as CommentMentionPayload | null)?.thread_subject ?? '',
    }),
    route: (payload) => ({
      path: ['/smart-inbox'],
      queryParams: {
        folder: 'mentions',
        include_thread_ids: (payload as CommentMentionPayload).thread_id,
      },
    }),
    desktop: false,
  },

  thread_assignment: {
    moduleLabelKey: null,
    titleKey: (payload) => {
      const assignment = payload as ThreadAssignmentPayload | null;
      return (assignment?.thread_ids?.length ?? 0) > 1
        ? 'notifications.event.thread_assignment_plural'
        : 'notifications.event.thread_assignment';
    },
    titleParams: (payload) => ({
      count: (payload as ThreadAssignmentPayload | null)?.thread_ids?.length ?? 1,
    }),
    route: (payload) => {
      const assignment = payload as ThreadAssignmentPayload;
      const queryParams: Record<string, string | number> = { assigned_to: 'me' };
      if (assignment.thread_ids.length === 1) {
        queryParams['include_thread_ids'] = assignment.thread_ids[0];
      }
      return { path: ['/smart-inbox'], queryParams };
    },
    desktop: false,
  },

  order_created: {
    moduleLabelKey: null,
    titleKey: (payload) => {
      const order = payload as OrderCreatedPayload | null;
      return order?.thread_subject
        ? 'notifications.event.order_created_subject'
        : 'notifications.event.order_created';
    },
    titleParams: (payload) => ({
      subject: (payload as OrderCreatedPayload | null)?.thread_subject ?? '',
    }),
    route: (payload) => ({
      path: ['/smart-inbox'],
      queryParams: { include_thread_ids: (payload as OrderCreatedPayload).thread_id },
    }),
    desktop: false,
  },

  order_status_changed: {
    // Order lifecycle events are persisted under the smart_inbox source but
    // belong to the orders surface — label them accordingly.
    moduleLabelKey: 'notifications.module.orders',
    titleKey: (payload) => {
      const order = payload as OrderStatusChangedPayload | null;
      const named = !!order?.order_name;
      const hasSubject = !!order?.email_subject;
      switch (order?.new_status) {
        case 'needs_review':
          if (named) return 'notifications.event.order_needs_review_named';
          return hasSubject
            ? 'notifications.event.order_needs_review_subject'
            : 'notifications.event.order_needs_review';
        case 'processing':
          if (named) return 'notifications.event.order_processing_named';
          return hasSubject
            ? 'notifications.event.order_processing_subject'
            : 'notifications.event.order_processing';
        default:
          return orderReadyTitleKey(order);
      }
    },
    titleParams: (payload) => orderStatusTitleParams(payload as OrderStatusChangedPayload | null),
    route: (payload) => {
      const order = payload as OrderStatusChangedPayload;
      // A completed placeholder resolves to a real order with a detail page;
      // processing / needs_review rows only exist inline in the list.
      if (order.new_status === 'completed' && order.order_id) {
        return { path: ['/quotations', order.order_id] };
      }
      return { path: ['/quotations'] };
    },
    desktop: {
      eligible: (payload) => (payload as OrderStatusChangedPayload).new_status === 'completed',
      // Same key and params as the bell row above — one copy, two surfaces.
      titleKey: (payload) => orderReadyTitleKey(payload as OrderStatusChangedPayload),
      titleParams: (payload) => orderStatusTitleParams(payload as OrderStatusChangedPayload),
      tag: (payload) => `volta.order.${(payload as OrderStatusChangedPayload).order_id}`,
      // Both list routes refresh themselves on this event; the detail route does not.
      suppressOnRoutes: ['/quotations', '/orders'],
    },
  },

  workflow_send_reply_failed: {
    moduleLabelKey: null,
    titleKey: (payload) => {
      const failure = payload as WorkflowSendReplyFailedPayload | null;
      return failure?.thread_subject
        ? 'notifications.event.workflow_send_reply_failed_subject'
        : 'notifications.event.workflow_send_reply_failed';
    },
    titleParams: (payload) => {
      const failure = payload as WorkflowSendReplyFailedPayload | null;
      return { subject: failure?.thread_subject ?? '', ruleName: failure?.rule_name ?? '' };
    },
    // Open the thread (as before) and float the draft the automation created
    // on top of it, so the user completes it in the conversation's context.
    route: (payload) => {
      const failure = payload as WorkflowSendReplyFailedPayload;
      return {
        path: ['/smart-inbox'],
        queryParams: { include_thread_ids: failure.thread_id, open_draft_id: failure.draft_id },
      };
    },
    desktop: false,
  },
};

// ── Lookups ──────────────────────────────────────────────────────────────────

/** Undefined for an event type the frontend does not know about yet. */
export function descriptorFor(
  eventType: NotificationEventType,
): NotificationEventDescriptor | undefined {
  return NOTIFICATION_EVENTS[eventType];
}

export function moduleLabelKey(notification: INotificationItem): string {
  return (
    descriptorFor(notification.event_type)?.moduleLabelKey ??
    `notifications.module.${notification.source_module}`
  );
}

export function titleKey(notification: INotificationItem): string {
  const descriptor = descriptorFor(notification.event_type);
  return descriptor ? descriptor.titleKey(notification.payload) : UNKNOWN_TITLE_KEY;
}

export function titleParams(notification: INotificationItem): Record<string, unknown> {
  const descriptor = descriptorFor(notification.event_type);
  return descriptor ? descriptor.titleParams(notification.payload) : {};
}
